import { toast } from 'react-toastify'
import React, { useCallback, useState } from 'react'
import { postCPData, putCPDataWithParams } from 'src/clinic/services'
import { buildTitleForAPrescriptionTemplate } from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/prescriptionCreateUtils'
import {
  buildPrescriptionTemplatePayload,
  normalizePrescriptionTemplateFromApi,
  getDefaultNewPrescriptionTemplatePayload,
  PRESCRIPTION_TEMPLATE_TYPE_PDF,
} from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/prescriptionTemplatePayload'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'

export default function usePrescriptionTemplateApiActions({
  selectedTemplateType,
  paperPrescriptionTemplate,
  pdfPrescriptionTemplate,
  setPaperPrescriptionTemplate,
  setPdfPrescriptionTemplate,
  setPdfOptions,
  setPaperOptions,
  setConfirmationModalProps,
  clinicId,
  mentorClinicAssociationData,
  clinicBrands,
  mentorProfileData,
}) {
  const [loading, setLoading] = useState()
  const [saving, setSaving] = useState(false)

  const relevantTemplate =
    selectedTemplateType === 'print' ? paperPrescriptionTemplate : pdfPrescriptionTemplate
  const relevantSetTemplate =
    selectedTemplateType === 'print' ? setPaperPrescriptionTemplate : setPdfPrescriptionTemplate

  function setTemplateData(
    relevantTemplates,
    setTemplateOptions,
    setTemplateData,
    silentlyUpdateData = false,
  ) {
    const templates = relevantTemplates?.map((template, idx) => {
      const normalized = normalizePrescriptionTemplateFromApi(template)
      const clinicLocation = mentorClinicAssociationData?.find(
        (location) => location?.id === template?.clinicId,
      )
      const clinicBrand = clinicBrands?.find((brand) => brand?.id === template?.clinicBrandId)
      const mentorProfile = mentorProfileData
      return {
        ...normalized,
        title: buildTitleForAPrescriptionTemplate({
          clinicLocationData: clinicLocation,
          clinicBrandData: clinicBrand,
          mentorProfileData: mentorProfile,
          idx: idx,
          prescriptionTemplate: normalized,
        }),
      }
    })
    const activeTemplate = templates?.find((template) => template?.active)
    setTemplateOptions(templates || [])
    if (silentlyUpdateData) {
      return
    }
    if (activeTemplate) {
      setTemplateData(activeTemplate)
    } else {
      setTemplateData(templates?.[0] || {})
    }
  }

  async function getAndSetPrescriptionTemplate(
    prescriptionTemplateId,
    enableSilentRefresh = false,
    languageCode,
  ) {
    if (!enableSilentRefresh) {
      setLoading(true)
    }
    const params = { clinicId: clinicId === 'private' ? null : clinicId }
    if (languageCode) params.languageCode = languageCode
    const response = await getMPDataWithParams('/api/secure/doctor/prescription-template', params)
    if (!enableSilentRefresh) {
      setLoading(false)
    }
    if (response?.status === 200) {
      let templates = response?.data || []
      if (response?.data?.data && Array.isArray(response.data.data)) {
        templates = response.data.data
      } else if (response?.data?.templates && Array.isArray(response.data.templates)) {
        templates = response.data.templates
      } else if (response?.data?.prescriptionTemplates && Array.isArray(response.data.prescriptionTemplates)) {
        templates = response.data.prescriptionTemplates
      }
      const pdfTemplates =
        templates?.filter((template) => template?.prescriptionTemplateType === 'pdf') || []
      const paperTemplates =
        templates?.filter(
          (template) =>
            template?.prescriptionTemplateType === 'print' || !template?.prescriptionTemplateType,
        ) || []
      setTemplateData(pdfTemplates, setPdfOptions, setPdfPrescriptionTemplate, enableSilentRefresh)
      setTemplateData(
        paperTemplates,
        setPaperOptions,
        setPaperPrescriptionTemplate,
        enableSilentRefresh,
      )
    }
  }

  async function savePrescriptionTemplate({ enableSilentRefresh = false }) {
    setSaving(true)
    try {
      const templateToSave = buildPrescriptionTemplatePayload({
        ...relevantTemplate,
        prescriptionTemplateType: selectedTemplateType,
      })
      const response = await savePrescriptionTemplateApiCall({
        prescriptionTemplate: templateToSave,
        clinicId,
      })
      const responseData = getDataMapFromCommonResponse(response)
      const saved = responseData?.['prescription-template']
      relevantSetTemplate(saved ? normalizePrescriptionTemplateFromApi(saved) : saved)
      getAndSetPrescriptionTemplate(undefined, enableSilentRefresh)
      toast.success('Saved Prescription Template')
    } catch (error) {
      toast.error('Failed to save prescription template')
      console.error('Failed to save prescription template:', error)
      // Handle error - could show a toast/notification here
    } finally {
      if (!enableSilentRefresh) {
        setSaving(false)
      }
    }
  }

  async function activatePrescriptionTemplate() {
    setSaving(true)
    try {
      await activatePrescriptionTemplateApiCall({
        prescriptionTemplate: relevantTemplate,
        clinicId,
      })
      getAndSetPrescriptionTemplate()
    } catch (error) {
      toast.error('Failed to activate prescription template')
      console.error('Failed to activate prescription template:', error)
      // Handle error - could show a toast/notification here
    } finally {
      setSaving(false)
    }
  }

  async function createPrescriptionTemplate() {
    setSaving(true)
    try {
      const response = await createPrescriptionTemplateApiCall({ clinicId })
      getAndSetPrescriptionTemplate()
      relevantSetTemplate(response?.data)
      toast.success('Prescription template created successfully')
      return response
    } catch (error) {
      toast.error('Failed to create prescription template')
      console.error('Failed to create prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  async function deletePrescriptionTemplate() {
    setSaving(true)
    let templateToSave = { ...relevantTemplate }
    try {
      await deletePrescriptionTemplateApiCall({
        templateId: templateToSave?.id,
      })
      getAndSetPrescriptionTemplate(undefined, true)
      toast.success('Prescription template deleted successfully')
    } catch (error) {
      toast.error('Failed to delete prescription template')
      console.error('Failed to delete prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  // Handle form submission - show confirmation modal
  const handleSubmit = () =>
    setConfirmationModalProps({
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'save',
      body: 'Do you want to save this prescription pad?',
      buttonColor: 'success',
      onSubmitFunctions: [() => savePrescriptionTemplate({ enableSilentRefresh: true })],
    })

  function handleDelete() {
    setConfirmationModalProps({
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'delete',
      body: 'Are you sure you want to delete this prescription pad? This action cannot be undone.',
      buttonColor: 'danger',
      onSubmitFunctions: [deletePrescriptionTemplate],
    })
  }

  return {
    getAndSetPrescriptionTemplate,
    savePrescriptionTemplate,
    addTemplate: createPrescriptionTemplate,
    deleteTemplate: handleDelete,
    activateTemplate: activatePrescriptionTemplate,
    loading,
    handleSubmit,
  }
}

export async function savePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId }) {
  const payload = buildPrescriptionTemplatePayload(prescriptionTemplate)
  const response = await postCPData('/api/secure/doctor/prescription-template/save', {
    ...payload,
    apiParam: 'prescription-template',
    clinicId: clinicId === 'private' ? null : clinicId,
  })
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function activatePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId }) {
  const response = await putCPDataWithParams(
    '/api/secure/doctor/prescription-template/activate',
    null,
    {
      'template-id': prescriptionTemplate.id,
    },
  )
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
  // try {
  //   const resp = await savePrescriptionTemplateApiCall({
  //     prescriptionTemplate: {
  //       ...prescriptionTemplate,
  //       active: true,
  //     },
  //     clinicId,
  //   })
  //   return Promise.resolve(resp)
  // } catch (error) {
  //   return Promise.reject(error)
  // }
}

async function createPrescriptionTemplateApiCall({ clinicId, templateType }) {
  const payload = getDefaultNewPrescriptionTemplatePayload({
    prescriptionTemplateType: templateType || PRESCRIPTION_TEMPLATE_TYPE_PDF,
    clinicId: clinicId === 'private' ? null : clinicId,
  })
  const response = await postCPData('/api/secure/doctor/prescription-template/save', {
    ...payload,
    apiParam: 'prescription-template',
  })
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function deletePrescriptionTemplateApiCall({ templateId }) {
  const response = await putCPDataWithParams(
    `/api/secure/doctor/prescription/prescription-template/disable`,
    {
      apiParam: 'prescription-template',
    },
    {
      'template-id': templateId,
    },
  )
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}
